import { AppStatus, Borrower } from "@/types/borrower";

/** A workflow board column: a labelled group of one or more app statuses. */
export interface WorkflowBucket {
  /** Stable key used for React keys and e2e test ids. */
  key: string;
  label: string;
  /** App statuses that belong in this bucket. */
  statuses: AppStatus[];
  /** Accent color (kept in sync with DealStatusBadge BADGE_CONFIG). */
  color: string;
}

/** A workflow bucket plus the borrowers that fall into it. */
export interface WorkflowBucketGroup extends WorkflowBucket {
  borrowers: Borrower[];
}

/** Inclusive calendar-day range used to filter the board. */
export interface DateRangeFilter {
  from?: Date;
  to?: Date;
}

/**
 * Ordered workflow buckets. "Waiting on Merchant" holds every post-approval
 * merchant-action status (contract ready through Plaid done); cards still
 * show the individual status chip so nothing is lost by collapsing columns.
 */
export const WORKFLOW_BUCKETS: WorkflowBucket[] = [
  {
    key: "new_apps",
    label: "New / Processing",
    // "Processing" is the AI analysis: an application moves submitted ->
    // ai_analyzing as soon as its statements are ready and stays there for the
    // whole run, so it must not vanish from the board until Initial Review.
    statuses: ["submitted", "ai_analyzing"],
    color: "#2563eb",
  },
  { key: "needs_input", label: "Needs Input", statuses: ["needs_input"], color: "#eab308" },
  { key: "initial_review", label: "Initial Review", statuses: ["funder_review"], color: "#2563eb" },
  {
    key: "waiting_on_merchant",
    label: "Waiting on Merchant",
    statuses: [
      "contract_ready",
      "contract_sent",
      "contract_signed",
      "bank_verification_required",
      "plaid_initiated",
      "plaid_completed",
    ],
    color: "#b45309",
  },
  {
    key: "final_underwriting",
    label: "Final Underwriting",
    statuses: ["final_underwriting"],
    color: "#2563eb",
  },
];

/** The timestamp used for ordering / date filtering, falling back to created_at. */
function statusTimestamp(borrower: Borrower): string | undefined {
  return borrower.last_status_changed_at ?? borrower.created_at;
}

function startOfDayMs(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * Returns true when `dateIso` falls within the (inclusive, calendar-day) range.
 * An empty/undefined range matches everything; a missing date never matches a
 * set range.
 */
export function isWithinDateRange(
  dateIso: string | null | undefined,
  range?: DateRangeFilter
): boolean {
  if (!range || (!range.from && !range.to)) return true;
  if (!dateIso) return false;

  const parsed = new Date(dateIso);
  if (Number.isNaN(parsed.getTime())) return false;

  const dayMs = startOfDayMs(parsed);
  if (range.from && dayMs < startOfDayMs(range.from)) return false;
  if (range.to && dayMs > startOfDayMs(range.to)) return false;
  return true;
}

/**
 * Groups borrowers into the ordered workflow buckets.
 *
 * - Applies the optional `range` filter on `created_at` (the server filters on
 *   the same field for `?view=workflow`; this keeps client and server aligned).
 * - Statuses outside every bucket (e.g. funded, declined, repayment) are
 *   dropped entirely.
 * - Each bucket is sorted by status timestamp descending (most recent first).
 */
export function groupAppsIntoBuckets(
  borrowers: Borrower[],
  range?: DateRangeFilter
): WorkflowBucketGroup[] {
  const filtered = borrowers.filter((b) => isWithinDateRange(b.created_at, range));

  return WORKFLOW_BUCKETS.map((bucket) => {
    const statusSet = new Set<AppStatus>(bucket.statuses);
    const matched = filtered
      .filter((b) => statusSet.has(b.app_status))
      .sort((a, b) => {
        const aTime = statusTimestamp(a) ? new Date(statusTimestamp(a) as string).getTime() : 0;
        const bTime = statusTimestamp(b) ? new Date(statusTimestamp(b) as string).getTime() : 0;
        return bTime - aTime;
      });

    return { ...bucket, borrowers: matched };
  });
}
